/**
 * Narrative Guidance admin page (SmartImprove) — Feb 2026.
 *
 * Lightweight enterprise narrative-governance layer. Controls the
 * consulting tone, executive framing, and narrative guidance used
 * across SmartImprove insights, recommendations, ROI commentary, and
 * executive summaries.
 *
 * Explicitly NOT a prompt-engineering playground:
 *   - no model / provider settings
 *   - no temperature / token / system-prompt controls
 *   - no RAG / vector / orchestration knobs
 *
 * The collection (`smartimprove_ai_guidance`) and API slug
 * (`/ai-guidance`) are preserved at the storage layer for backward
 * compatibility with the deterministic narrative engine and existing
 * seeded rows. Legacy `target_stage` values are normalised by the
 * backend on read/write so admins never see the old taxonomy.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, BookOpen } from "lucide-react";
import { aiGuidanceApi } from "../../lib/smartimproveAdminApi";

// ---------------------------------------------------------------------------
// Curated taxonomy — stays in sync with `_validate_ai_rule` on the backend.
// Both lists are intentionally finite and consulting-grade. Future taxonomy
// additions should land in BOTH places (backend allow-list + this file).
// ---------------------------------------------------------------------------
const APPLIES_TO = [
  { value: "insights", label: "Insights" },
  { value: "recommendations", label: "Recommendations" },
  { value: "roi_commentary", label: "ROI Commentary" },
  { value: "executive_summary", label: "Executive Summary" },
  { value: "implementation_roadmap", label: "Implementation Roadmap" },
  { value: "transformation_narrative", label: "Transformation Narrative" },
  { value: "client_communication", label: "Client Communication" },
];

const TONES = [
  {
    value: "consulting",
    label: "Consulting",
    chip: "bg-[#EEF2F5] text-[#475569] border-[#CBD5E1]",
  },
  {
    value: "executive",
    label: "Executive",
    chip: "bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]",
  },
  {
    value: "operational",
    label: "Operational",
    chip: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  },
  {
    value: "strategic",
    label: "Strategic",
    chip: "bg-[#E0F2FE] text-[#0369A1] border-[#BAE6FD]",
  },
  {
    value: "transformation",
    label: "Transformation",
    chip: "bg-[#CCFBF1] text-[#0F766E] border-[#99F6E4]",
  },
  {
    value: "compliance_aware",
    label: "Compliance-Aware",
    chip: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  },
  {
    value: "workshop_facilitation",
    label: "Workshop Facilitation",
    chip: "bg-[#F0EDF9] text-[#473F8B] border-[#DDD6FE]",
  },
];

const APPLIES_LABEL = Object.fromEntries(APPLIES_TO.map((a) => [a.value, a.label]));
const TONE_BY_VALUE = Object.fromEntries(TONES.map((t) => [t.value, t]));

function slugify(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

const EMPTY = {
  id: null,
  slug: "",
  name: "",
  target_stage: "insights",
  tone: "consulting",
  instructions: "",
  active: true,
  sort_order: 0,
};

function ToneChip({ value }) {
  const t = TONE_BY_VALUE[value];
  if (!t) return null;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm ${t.chip}`}
      data-testid={`ng-tone-chip-${value}`}
    >
      {t.label}
    </span>
  );
}

function GuidanceDialog({ open, onOpenChange, value, onChange, onSave, saving }) {
  const isEdit = !!value?.id;
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ng-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Narrative Guidance" : "New Narrative Guidance"}
          </DialogTitle>
          <DialogDescription>
            Controls how SmartImprove narratives are framed — consulting tone,
            executive voice, compliance positioning. Not a prompt-engineering
            tool.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Rule Name
            </label>
            <Input
              value={value?.name || ""}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="e.g. Executive Summary — CFO-ready tone"
              className="rounded-sm text-xs"
              data-testid="ng-field-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1">
                Applies To
              </label>
              <Select
                value={value?.target_stage || "insights"}
                onValueChange={(v) => set({ target_stage: v })}
              >
                <SelectTrigger
                  className="rounded-sm text-xs"
                  data-testid="ng-field-applies-to"
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {APPLIES_TO.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1">
                Tone
              </label>
              <Select
                value={value?.tone || "consulting"}
                onValueChange={(v) => set({ tone: v })}
              >
                <SelectTrigger
                  className="rounded-sm text-xs"
                  data-testid="ng-field-tone"
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {TONES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Guidance Text
            </label>
            <Textarea
              value={value?.instructions || ""}
              onChange={(e) => set({ instructions: e.target.value })}
              rows={5}
              placeholder="e.g. Open with the strategic narrative, then quantified impact, then phased roadmap. Avoid technical jargon. Never guarantee ROI."
              className="rounded-sm text-xs leading-[1.55]"
              data-testid="ng-field-guidance"
            />
            <p className="text-[10px] text-[#94A3B8] mt-1">
              Plain consulting English. Used by deterministic narrative engines
              and (future) AI-assisted summaries.
            </p>
          </div>
          <div className="flex items-center gap-6 pt-1">
            <div className="flex items-center gap-2">
              <input
                id="ng-active"
                type="checkbox"
                checked={value?.active !== false}
                onChange={(e) => set({ active: e.target.checked })}
                data-testid="ng-field-active"
              />
              <label htmlFor="ng-active" className="text-xs text-[#374151]">
                Active
              </label>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#374151]">Sort order</label>
              <Input
                type="number"
                value={value?.sort_order ?? 0}
                onChange={(e) => set({ sort_order: e.target.value })}
                className="rounded-sm text-xs w-24"
                data-testid="ng-field-sort-order"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !value?.name?.trim()}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="ng-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function NarrativeGuidancePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filterApplies, setFilterApplies] = useState("__all");
  const [filterTone, setFilterTone] = useState("__all");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const items = await aiGuidanceApi.list();
      setRows(items || []);
    } catch (e) {
      toast.error("Failed to load Narrative Guidance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const sortedRows = useMemo(() => {
    const filtered = rows.filter((r) => {
      if (
        filterApplies !== "__all" &&
        (r.target_stage || "") !== filterApplies
      )
        return false;
      if (filterTone !== "__all" && (r.tone || "") !== filterTone) return false;
      return true;
    });
    return [...filtered].sort(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.name).localeCompare(String(b.name))
    );
  }, [rows, filterApplies, filterTone]);

  const openCreate = () => {
    setForm({ ...EMPTY });
    setDialog(true);
  };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      slug: row.slug || "",
      name: row.name || "",
      target_stage: row.target_stage || "insights",
      tone: row.tone || "consulting",
      instructions: row.instructions || "",
      active: row.active !== false,
      sort_order: row.sort_order ?? 0,
    });
    setDialog(true);
  };

  const save = async () => {
    const isEdit = !!form.id;
    const name = (form.name || "").trim();
    if (!name) return;
    const slug = isEdit ? form.slug : slugify(name);
    const payload = {
      slug,
      name,
      target_stage: form.target_stage || "insights",
      tone: form.tone || "consulting",
      instructions: (form.instructions || "").trim(),
      active: !!form.active,
      sort_order: Number(form.sort_order) || 0,
    };
    setSaving(true);
    try {
      if (isEdit) await aiGuidanceApi.update(form.id, payload);
      else await aiGuidanceApi.create(payload);
      toast.success(`Narrative guidance ${isEdit ? "updated" : "created"}`);
      setDialog(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await aiGuidanceApi.remove(row.id);
      toast.success("Narrative guidance deleted");
      await reload();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-5" data-testid="si-narrative-guidance">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Narrative Governance
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Narrative Guidance
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-3xl">
            Controls the consulting tone, executive framing, and narrative
            guidance used across SmartImprove insights, recommendations, ROI
            commentary, and executive summaries.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs whitespace-nowrap"
          data-testid="ng-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Guidance
        </Button>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#6B7280] mr-1">
          Filters
        </span>
        <Select value={filterApplies} onValueChange={setFilterApplies}>
          <SelectTrigger
            className="rounded-sm text-xs w-56"
            data-testid="ng-filter-applies"
          >
            <SelectValue placeholder="Applies To" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All Surfaces</SelectItem>
            {APPLIES_TO.map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterTone} onValueChange={setFilterTone}>
          <SelectTrigger
            className="rounded-sm text-xs w-56"
            data-testid="ng-filter-tone"
          >
            <SelectValue placeholder="Tone" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All Tones</SelectItem>
            {TONES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#6B7280] ml-auto">
          {sortedRows.length} rule{sortedRows.length === 1 ? "" : "s"}
        </span>
      </div>

      {loading ? (
        <div className="px-5 py-8 text-sm text-[#6B7280]">Loading…</div>
      ) : sortedRows.length === 0 ? (
        <div
          className="bg-white border border-[#E5E7EB] rounded-sm px-5 py-12 text-center text-sm text-[#6B7280]"
          data-testid="ng-empty"
        >
          No guidance rules match the current filters. Click{" "}
          <strong>Add Guidance</strong> to create one.
        </div>
      ) : (
        <div
          className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden"
          data-testid="ng-table"
        >
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
              <tr>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold tracking-[0.14em] uppercase text-[#6B7280] w-[34%]">
                  Rule Name
                </th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold tracking-[0.14em] uppercase text-[#6B7280]">
                  Applies To
                </th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold tracking-[0.14em] uppercase text-[#6B7280]">
                  Tone
                </th>
                <th className="px-4 py-2.5 text-left text-[10px] font-semibold tracking-[0.14em] uppercase text-[#6B7280]">
                  Status
                </th>
                <th className="px-4 py-2.5 text-right text-[10px] font-semibold tracking-[0.14em] uppercase text-[#6B7280] w-[80px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr
                  key={row.id}
                  className={`border-b border-[#F1F5F9] last:border-0 hover:bg-[#FAFBFC] ${
                    row.active === false ? "opacity-60" : ""
                  }`}
                  data-testid={`ng-row-${row.id}`}
                >
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-start gap-2">
                      <BookOpen className="w-3.5 h-3.5 mt-0.5 text-[#025979] shrink-0" />
                      <div>
                        <p
                          className="text-[13px] font-semibold text-[#111827]"
                          style={{ fontFamily: "Chivo, sans-serif" }}
                        >
                          {row.name}
                        </p>
                        {row.instructions ? (
                          <p className="text-[11px] text-[#6B7280] mt-0.5 line-clamp-2 max-w-[420px]">
                            {row.instructions}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className="text-[11px] text-[#475569]">
                      {APPLIES_LABEL[row.target_stage] || row.target_stage || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <ToneChip value={row.tone} />
                  </td>
                  <td className="px-4 py-3 align-top">
                    {row.active === false ? (
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
                        inactive
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => openEdit(row)}
                        className="text-[#025979] hover:text-[#03738C]"
                        data-testid={`ng-edit-${row.id}`}
                        aria-label="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(row)}
                        className="text-[#B91C1C] hover:text-[#7F1D1D]"
                        data-testid={`ng-delete-${row.id}`}
                        aria-label="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <GuidanceDialog
        open={dialog}
        onOpenChange={setDialog}
        value={form}
        onChange={setForm}
        onSave={save}
        saving={saving}
      />
    </div>
  );
}
